/*
 * <fb-infinite-grid> - endless product grid. When the visitor nears the end, the next
 * page of cards is fetched and appended: no page numbers. Used by
 *   sections/main-collection-product-grid.liquid  data-source="section" (Section Rendering API)
 *   sections/featured-collection.liquid           data-source="view"    (/collections/x?view=fb-cards)
 * Both responses carry <ul data-fb-cards data-fb-next="..."> with <li data-product-id>.
 * Cards already shown are skipped, so the homepage can start from page 1.
 * Ends with a note; a failed fetch shows "Load more"; the back button restores.
 */
(() => {
  if (customElements.get('fb-infinite-grid')) return;
  const STORE = 'fb-infinite:';

  class FbInfiniteGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-fb-cards]');
      if (!this.list) return;
      this.next = this.dataset.next || '';
      this.ids = new Set([...this.list.children].map((li) => li.dataset.productId).filter(Boolean));
      this.sentinel = this.querySelector('[data-fb-sentinel]');
      this.status = this.querySelector('[data-fb-status]');
      this.endNote = this.querySelector('[data-fb-end]');
      this.more = this.querySelector('[data-fb-more]');
      this.key = `${STORE}${location.pathname}${location.search}#${this.dataset.sourceId || ''}`;
      this.abort = new AbortController();
      const opt = { signal: this.abort.signal };
      this.classList.add('is-enhanced');
      this.more?.addEventListener('click', () => {
        this.failed = false;
        this.load();
      }, opt);
      window.addEventListener('pagehide', () => this.save(), opt);
      if ('IntersectionObserver' in window) {
        this.revealIo = new IntersectionObserver((entries) => this.onReveal(entries), { rootMargin: '0px 0px -50px 0px' });
      }
      this.restore();
      if (!this.next) {
        this.finish();
        return;
      }
      if ('IntersectionObserver' in window && this.sentinel) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.load();
        }, { rootMargin: '0px 0px 100% 0px' });
        this.io.observe(this.sentinel);
        // An observer only reports changes: a jump past the sentinel in one go (End key,
        // scrollbar drag, a long fling) goes from "below" to "above" with no change to
        // report, and nothing loaded (found on the preview, 2026-10-13). One passive
        // check per frame catches that; sentinelNear() counts "above" as near.
        window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.abort.signal });
      } else {
        this.showMore();
      }
    }

    onScroll() {
      if (this.ticking || !this.next || this.failed) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        if (this.sentinelNear()) this.load();
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.io?.disconnect();
      this.revealIo?.disconnect();
    }

    requestUrl() {
      const url = new URL(this.next, location.origin);
      if (this.dataset.source === 'section') url.searchParams.set('section_id', this.dataset.sourceId);
      else url.searchParams.set('view', this.dataset.sourceId);
      return url.toString();
    }

    async load() {
      if (this.loading || !this.next || this.failed) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      if (this.more) this.more.hidden = true;
      try {
        const res = await fetch(this.requestUrl(), { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const source = doc.querySelector('[data-fb-cards]');
        if (!source) throw new Error('no cards in the response');
        const before = this.next;
        const added = this.append([...source.children]);
        this.next = source.dataset.fbNext || '';
        if (this.next === before) this.next = ''; // a page pointing at itself would loop
        this.announce(added);
        this.save();
        if (!this.next) this.finish();
      } catch (error) {
        this.showMore();
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
      // The sentinel may still be on screen: the observer does not fire again until it leaves.
      if (this.next && !this.failed && this.sentinelNear()) this.load();
    }

    append(items) {
      const fragment = document.createDocumentFragment();
      const fresh = [];
      for (const li of items) {
        const id = li.dataset ? li.dataset.productId : '';
        if (!id || this.ids.has(id)) continue;
        this.ids.add(id);
        const node = document.importNode(li, true);
        if (this.revealIo && node.classList.contains('scroll-trigger')) node.classList.add('scroll-trigger--offscreen');
        fresh.push(node);
        fragment.append(node);
      }
      this.list.append(fragment);
      if (this.revealIo) fresh.forEach((node) => node.classList.contains('scroll-trigger') && this.revealIo.observe(node));
      return fresh.length;
    }

    onReveal(entries) {
      // Dawn's own reveal (assets/animations.js), for the appended cards only.
      entries.forEach((entry, index) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('scroll-trigger--offscreen');
        if (entry.target.hasAttribute('data-cascade')) entry.target.style.setProperty('--animation-order', index);
        this.revealIo.unobserve(entry.target);
      });
    }

    announce(count) {
      if (!this.status || count < 1) return;
      this.status.textContent = (this.dataset.loadedText || '').replace('__COUNT__', String(count));
    }

    finish() {
      this.io?.disconnect();
      this.next = '';
      if (this.more) this.more.hidden = true;
      if (this.endNote) this.endNote.hidden = false;
      this.toggleAttribute('data-done', true);
    }

    showMore() {
      this.failed = true;
      if (this.more) this.more.hidden = false;
    }

    sentinelNear() {
      const r = this.sentinel?.getBoundingClientRect();
      return !!r && r.top < window.innerHeight * 2;
    }

    save() {
      try {
        const appended = [...this.list.children].slice(this.initialCount).map((li) => li.outerHTML);
        sessionStorage.setItem(this.key, JSON.stringify({ next: this.next, items: appended, y: window.scrollY }));
      } catch (error) {
        /* storage full or blocked: the page works without it */
      }
    }

    restore() {
      this.initialCount = this.list.children.length;
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.key) || 'null');
      } catch (error) {
        saved = null;
      }
      const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      if (!saved || !nav || nav.type !== 'back_forward') {
        try {
          sessionStorage.removeItem(this.key);
        } catch (error) {
          /* ignore */
        }
        return;
      }
      // Back navigation is a fresh load here (Shopify pages skip the bfcache): put the
      // cards back first; the browser's own scroll restoration then finds the place
      // (tests/e2e: test_the_back_button_restores_the_list_and_the_place). The
      // scrollTo is a fallback for browsers that restore before the cards are back.
      const tpl = document.createElement('template');
      tpl.innerHTML = saved.items.join('');
      this.append([...tpl.content.children]);
      this.next = saved.next || '';
      requestAnimationFrame(() => window.scrollTo(0, saved.y || 0));
    }
  }

  customElements.define('fb-infinite-grid', FbInfiniteGrid);
})();
